import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { isEmpty, isNil } from 'lodash'

import { DEFAULT_EMANDATE_METHOD, PROGRAMS_MAPPING } from './constants'

dayjs.extend(customParseFormat)

export const composeBatchDetails = (selectedSchedule) => {
  if (!selectedSchedule) return {}

  const {
    uuid,
    name: batchName,
    centre,
    enrolment_capacity: enrolmentCapacity,
    course,
    teacher,
    cadence: alternateBatches,
    start_time: startTime,
    end_time: endTime,
    end_date,
    start_date,
    fee,
    vacancy,
    schedule_event,
    type,
    level,
    coins_per_session: batchCoinsPerSession,
    coinsTotal,
    coinsNoOfSessions,
    coinsBySession,
    batchStartDate,
    batchEndDate,
    creditsApplied,
    coinPredictionError,
    batchErrorType,
    isSameSlotBooked,
    coins_per_month,
    is_play_care: isPlayCare
  } = selectedSchedule

  const { name: centreName, city } = centre || {}

  const {
    name: courseName,
    details,
    min_age: minAge,
    max_age: maxAge,
    subjects,
    standards,
    coins_per_session: coinsPerSession
  } = course || {}

  const { full_name: teacherName } = teacher || {}

  const noOfClasses = schedule_event?.schedules?.length ?? 0

  const durationInDays = dayjs(end_date).diff(dayjs(start_date), 'days')
  const weeks = dayjs.duration({ days: durationInDays }).asWeeks()

  const durationInWeeks = Math.ceil(weeks)

  const firstCadence = alternateBatches?.[0]
  const end_time = firstCadence
    ? dayjs(firstCadence.end_time, 'HH-mm', false)
    : null
  const start_time = firstCadence
    ? dayjs(firstCadence.start_time, 'HH-mm', false)
    : null
  const totalMins = end_time && start_time ? end_time.diff(start_time, 'm') : 0

  const totalHoursPerSession = totalMins / 60

  const totalHours = noOfClasses * (totalMins / 60)

  const duration = Math.min(durationInWeeks, 4) || 1

  const totalSessions =
    type === 'membership' ? alternateBatches.length * duration : noOfClasses

  return {
    uuid,
    batchName,
    courseName,
    teacherName,
    details,
    location: `${centreName}${city ? `, ${city}` : ''}`,
    enrolmentCapacity,
    price: fee,
    coins: coinsPerSession,
    coins_monthly: coins_per_month,
    subject: subjects[0],
    vacancy,
    ...(startTime && endTime
      ? {
          timing: `${dayjs(startTime, 'HH:mm:ss', true).format('h:mm')}-${dayjs(
            endTime,
            'HH:mm:ss',
            true
          ).format('h:mm A')}`
        }
      : null),
    ...(!isEmpty(alternateBatches)
      ? {
          alternateBatches: alternateBatches.map(
            (time) =>
              `${dayjs()
                .day(time.weekday + 1)
                .format('ddd')} ${dayjs(time.start_time, 'HH-mm', false).format(
                'h:mm'
              )}-${dayjs(time.end_time, 'HH-mm', false).format('h:mm A')}`
          )
        }
      : null),
    ...(minAge && maxAge ? { ages: `${minAge}-${maxAge}` } : null),
    standards,
    durationInWeeks,
    totalHoursPerSession,
    totalHours,
    type,
    level,
    totalSessions,
    coinsTotal,
    coinsNoOfSessions,
    coinsBySession,
    batchCoinsPerSession,
    batchStartDate,
    batchEndDate,
    creditsApplied,
    coinPredictionError,
    batchErrorType,
    isPlayCare,
    isSameSlotBooked,
    subjectsName: subjects.map((subject) => subject.name).join(' • '),
    subjectsDetail: subjects.map((subject) => subject.description).join('\n')
  }
}

export const coinCalculationBodyStructure = (data) => {
  const { parent, cart_type } = data
  let body = { cart_type, user: parent }
  switch (cart_type) {
    case 'booking':
      body = { ...body, ...paymentOptionsItemsStructure(data?.cartItems) }
      break

    default:
      break
  }
  return body
}

export const categoryCostBodyStructure = (data) => {
  const {
    batch,
    startDate: { label: startDateLabel },
    isDiscover
  } = data

  return {
    batch,
    start_date: startDateLabel,
    product: isDiscover === true ? 'discovery' : 'subscription'
  }
}

export const paymentOptionsItemsStructure = (items) => {
  return {
    items: items.map((item) => {
      const product =
        item?.batchFormInfo?.isDiscover === true ? 'discovery' : 'subscription'
      const reason =
        item?.batchFormInfo?.isDiscover === true
          ? 'Discovery booking'
          : 'Subscription'
      const mappedItem = {
        product,
        reason,
        cost_price: item?.price,
        batch: item?.batchDetails?.uuid,
        student: item?.userInfo?.student?.user?.uuid,
        start_date: item?.batchFormInfo?.startDate?.label,
        end_date: item?.batchFormInfo?.endDate?.value,
        subject: item?.batchDetails?.course?.subjects?.[0]?.uuid,
        subject_internal_name:
          item?.batchDetails?.course?.subjects?.[0]?.internal_name
      }
      return mappedItem
    })
  }
}

// A ₹0 kit line is never a real kit. The backend used to auto-add one for
// every subscription, including subjects that ship no kit at all (Public
// Speaking, Chess, Music...), and Alfred echoed them straight back into the
// cart. Worse than clutter: a single ₹0 kit line in the cart makes the backend
// treat the client as the pricing authority and suppress the *real* kit for
// every other item, which is how one Robotics kit went unbilled. The API no
// longer returns them; this keeps the cart honest even if it does.
const pricedKitItems = (cartItems) =>
  (cartItems || []).filter(
    (item) =>
      item.product === 'kit' && Number(item.cost_price || item.value || 0) > 0
  )

export const bookEnrolmentBodyStructure = (data) => {
  const structure = {
    ...(data?.useCoins ? { coin_payment: data?.coinBalance } : null),
    user: data.parent,
    type: 'booking',
    payment_mode: data.payment_mode,
    notes: 'Purchase',
    value: data.cartTotal,
    items: [
      ...data.cartItems
        .filter((item) => item.product !== 'kit')
        .map((item) => ({
          cost_price: item.price,
          reason: item.batchFormInfo.isDiscover
            ? 'Discovery booking'
            : 'Subscription',
          product: item?.batchFormInfo?.isDiscover
            ? 'discovery'
            : 'subscription',
          batch: item?.batchDetails?.uuid,
          student: item.userInfo.student.user.uuid,
          start_date: item?.batchFormInfo?.startDate?.label,
          end_date: item?.batchFormInfo?.endDate?.value,
          subject: item?.batchDetails?.course?.subjects?.[0]?.uuid,
          subject_name: item?.batchDetails?.course?.subjects?.[0]?.name,
          subject_internal_name:
            item?.batchDetails?.course?.subjects?.[0]?.internal_name
        })),
      ...pricedKitItems(data.cartItems)
    ],
    ...(data.reason_for_using_alfred !== '' &&
    !isNil(data.reason_for_using_alfred)
      ? { reason_for_using_alfred: data.reason_for_using_alfred }
      : {})
  }
  if (data?.summary?.admission_fee > 0) {
    structure.items.push({
      cost_price: data?.summary?.admission_fee,
      discount: 0,
      reason: 'Admission',
      product: 'admission'
    })
  }
  return structure
}

// The cart lines, shared by every booking payload. Kit lines are already priced by
// the API and come through untouched; everything else is a batch booking.
const bookingCartItems = (cartItems) => [
  ...cartItems
    .filter((item) => item.product !== 'kit')
    .map((item) => ({
      cost_price: item.price,
      reason: item.batchFormInfo.isDiscover
        ? 'Discovery booking'
        : 'Subscription',
      product: item?.batchFormInfo?.isDiscover ? 'discovery' : 'subscription',
      batch: item?.batchDetails?.uuid,
      student: item.userInfo.student.user.uuid,
      start_date: item?.batchFormInfo?.startDate?.label,
      end_date: item?.batchFormInfo?.endDate?.value,
      subject: item?.batchDetails?.course?.subjects?.[0]?.uuid,
      subject_name: item?.batchDetails?.course?.subjects?.[0]?.name,
      subject_internal_name:
        item?.batchDetails?.course?.subjects?.[0]?.internal_name
    })),
  ...pricedKitItems(cartItems)
]

const withAdmissionFeeItem = (items, summary) => {
  if (summary?.admission_fee > 0) {
    items.push({
      cost_price: summary.admission_fee,
      discount: 0,
      reason: 'Admission',
      product: 'admission'
    })
  }
  return items
}

/**
 * "Book with e-Mandate" — the parent's existing mandate covers this cart.
 *
 * Sent as an ordinary `booking` cart; `payment_type` is what routes it to the v3
 * cart API and puts it on the pre-debit-notification path. Nothing is charged or
 * enrolled synchronously: the PDN goes out now and the debit follows ~26h later.
 */
export const bookEnrolmentWithEmandateBodyStructure = (data) => {
  const structure = {
    user: data.parent,
    type: 'booking',
    payment_mode: 'rupees',
    payment_type: 'book_with_emandate',
    notes: 'Purchase (e-mandate autopay)',
    value: data.cartTotal,
    items: bookingCartItems(data.cartItems),
    ...(data.reason_for_using_alfred !== '' &&
    !isNil(data.reason_for_using_alfred)
      ? { reason_for_using_alfred: data.reason_for_using_alfred }
      : {})
  }
  return {
    ...structure,
    items: withAdmissionFeeItem(structure.items, data?.summary)
  }
}

/**
 * "Setup e-Mandate & Book" — no usable mandate, so authorise a new one sized to the
 * cart + the recurring bill, and collect the prorated first payment on the same link.
 *
 * `mandate_max_amount` comes from the payment option the backend already sized; the
 * backend re-derives its own floor and takes the larger of the two, so a stale cart
 * can only ever ask for too much, never too little.
 */
export const setupEmandateAndBookBodyStructure = (data) => {
  const structure = {
    user: data.parent,
    type: 'e_mandate_booking',
    payment_mode: 'rupees',
    payment_type: 'setup_emandate_and_pay',
    notes: 'Purchase (e-mandate setup)',
    value: data.cartTotal,
    // Razorpay rejects a registration link with no rail ("Provide auth_type or method"),
    // and an unstated one resolves to netbanking/NACH — the slowest option. Say UPI.
    emandate: { method: data.emandateMethod || DEFAULT_EMANDATE_METHOD },
    ...(data?.mandateMaxAmount
      ? { mandate_max_amount: data.mandateMaxAmount }
      : {}),
    items: bookingCartItems(data.cartItems),
    ...(data.reason_for_using_alfred !== '' &&
    !isNil(data.reason_for_using_alfred)
      ? { reason_for_using_alfred: data.reason_for_using_alfred }
      : {})
  }
  return {
    ...structure,
    items: withAdmissionFeeItem(structure.items, data?.summary)
  }
}

export const bookEnrolmentWithRechargeBodyStructure = (data) => {
  const structure = {
    user: data.uuid,
    type: 'topup_booking',
    payment_mode: 'rupees',
    notes: 'Purchase coins and do booking',
    value: data.coins,
    items: [
      ...data.cartItems
        .filter((item) => item.product !== 'kit')
        .map((item) => ({
          cost_price: item.price,
          reason: item.batchFormInfo.isDiscover
            ? 'Discovery booking'
            : 'Subscription',
          product: item?.batchFormInfo?.isDiscover
            ? 'discovery'
            : 'subscription',
          batch: item?.batchDetails?.uuid,
          student: item.userInfo.student.user.uuid,
          start_date: item?.batchFormInfo?.startDate?.label,
          end_date: item?.batchFormInfo?.endDate?.value,
          subject: item?.batchDetails?.course?.subjects?.[0]?.uuid,
          subject_name: item?.batchDetails?.course?.subjects?.[0]?.name,
          subject_internal_name:
            item?.batchDetails?.course?.subjects?.[0]?.internal_name
        })),
      ...pricedKitItems(data.cartItems)
    ],
    ...(data.reason_for_using_alfred !== '' &&
    !isNil(data.reason_for_using_alfred)
      ? { reason_for_using_alfred: data.reason_for_using_alfred }
      : {})
  }

  structure.items.push({
    cost_price: data.coins,
    discount: data.discount_percentage,
    reason: 'Recharge',
    product: 'coins'
  })
  if (data?.summary?.admission_fee > 0) {
    structure.items.push({
      cost_price: data?.summary?.admission_fee,
      discount: 0,
      reason: 'Admission',
      product: 'admission'
    })
  }
  return structure
}

export const bookProgramBodyStructure = (data) => {
  // Feature 3 — daycare-billing flag flows per-item. The serializer on
  // the backend also accepts a cart-level `is_daycare_billing` field;
  // we set it to true when any item opted in so a single-item cart
  // still validates cleanly without repeating the flag per-item.
  const items = data.cartItems.map((item) => ({
    item_id: item.id,
    // cost_price: item.price,
    reason: 'Preschool Booking',
    product: item?.batchDetails?.type,
    program_fee: item?.batchFormInfo?.programFee,
    payment_cycle: item?.batchFormInfo?.paymentCycle?.value,
    additional_discounts: item?.batchFormInfo?.additionalDiscountOffered
      ? [item?.batchFormInfo?.additionalDiscount]
      : [],
    is_daycare_billing: Boolean(item?.batchFormInfo?.isDaycareBilling),
    batch: item?.batchDetails?.uuid,
    student: item.userInfo.student.user.uuid,
    start_date: item?.batchFormInfo?.startDate?.value,
    // end_date: item?.batchFormInfo?.endDate?.value,
    subject: item?.batchDetails?.course?.subjects?.[0]?.uuid,
    subject_name: item?.batchDetails?.course?.subjects?.[0]?.name,
    subject_internal_name:
      item?.batchDetails?.course?.subjects?.[0]?.internal_name
  }))
  const cartLevelDaycare = items.some((i) => i.is_daycare_billing)
  // Feature 5 — AIM-only seat blocking. Cart-level flag (mirrors the
  // daycare-billing pattern). When true, only AIM is charged; the
  // SCHOOL programme line still ships so the backend can lock the
  // batch + centre + variant on the admission record.
  const cartLevelSeatBlock = data.cartItems.some(
    (item) => item?.batchFormInfo?.isSeatBlock
  )

  // Charmi #5 (2026-05-25) — seat-block cart submissions were 400ing with
  // "Seat block cart must include the AIM fee". The backend validator
  // (process_invoice.py::_validate_seat_block_cart) requires an explicit
  // preschool_aim_fee line per SCHOOL item; the FE was only sending the
  // SCHOOL line. Mirror each SCHOOL item with an AIM line so the cart
  // passes validation. The backend computes the AIM amount from
  // program_fee.admission_fee, so we just need the linkage fields.
  if (cartLevelSeatBlock) {
    const schoolItems = data.cartItems.filter(
      (item) =>
        item?.batchFormInfo?.isSeatBlock &&
        item?.batchDetails?.type === 'school'
    )
    schoolItems.forEach((schoolItem) => {
      items.push({
        // Charmi #5 (2026-05-25) follow-up — backend validates each cart
        // item has a unique item_id (validate_cart_data → "No unique ID
        // has been paased in cart item") AND a batch ("Error! Batch not
        // found in cart items"). Mirror the school item's id (with -aim
        // suffix) and reuse the same batch UUID so the AIM line stays
        // linked to the SCHOOL row's batch lock.
        item_id: `${schoolItem.id}-aim`,
        product: 'preschool_aim_fee',
        reason: 'AIM (seat block)',
        program_fee: schoolItem?.batchFormInfo?.programFee,
        payment_cycle: schoolItem?.batchFormInfo?.paymentCycle?.value,
        is_daycare_billing: Boolean(
          schoolItem?.batchFormInfo?.isDaycareBilling
        ),
        batch: schoolItem?.batchDetails?.uuid,
        student: schoolItem.userInfo.student.user.uuid,
        start_date: schoolItem?.batchFormInfo?.startDate?.value
      })
    })
  }

  const structure = {
    user: data.parent,
    type: 'booking',
    cart_item_type: 'preschool',
    payment_mode: 'rupees',
    notes: 'Purchase program',
    is_daycare_billing: cartLevelDaycare,
    is_seat_block: cartLevelSeatBlock,
    items
  }

  return structure
}

const formatProgramConfigTimingSlot = (slot) => {
  if (!slot?.start_time || !slot?.end_time) return null

  const start = dayjs(slot.start_time, ['HH:mm:ss', 'HH-mm'], true)
  const end = dayjs(slot.end_time, ['HH:mm:ss', 'HH-mm'], true)
  const timeRange =
    start.isValid() && end.isValid()
      ? `${start.format('h:mm')}-${end.format('h:mm A')}`
      : `${slot.start_time} - ${slot.end_time}`

  return timeRange
}

export const transformProgramCards = (data) => {
  const { results } = data
  const transformedPrograms = results?.map((program) => {
    const {
      uuid,
      name,
      program_enrolment_capacity,
      standards_shortcode,
      image,
      timings: _timings
    } = program
    const timings = (_timings || [])
      .map(formatProgramConfigTimingSlot)
      .filter(Boolean)
      .join(' • ')
    const standards = []

    //Multiple program_enrolment_capacities, each have standards
    program_enrolment_capacity.forEach((pec) => {
      pec.standards.forEach((standard) => {
        //prevent repetitions over multiple program_enrolment_capacitiy objects
        if (standards.findIndex((_std) => standard.uuid == _std.uuid) === -1)
          standards.push(standard)
      })
    })

    // sorting the grades in an ascending order for each program
    const sortedStandards = standards.sort((a, b) => a.age - b.age)

    const standardsShortCodes = standards_shortcode
    //Changed standards to BE values. Keeping the old logic above
    const standardsShortCodesDisplay = standardsShortCodes.join(', ')

    const matchingProgram = PROGRAMS_MAPPING.find(
      (mappedProgram) => mappedProgram.name === name
    )
    const icon = matchingProgram?.icon || PROGRAMS_MAPPING?.[0]?.icon

    const endTime = _timings?.[0]?.end_time ?? ''
    return {
      uuid,
      name,
      timings,
      // standards,
      standardsShortCodes,
      standardsShortCodesDisplay,
      icon,
      image,
      endTime
    }
  })

  return transformedPrograms?.sort((a, b) => {
    if (a.endTime < b.endTime) return -1
    if (a.endTime > b.endTime) return 1
    return 0
  })
}

export const transformCartItemsPaymentSummaryPayload = (
  cartItems,
  parentUuid
) => {
  // Feature 5 — cart-level seat-block flag mirrors the cart-submit
  // payload so the preview total matches what process_invoice produces.
  // Without this, the slider shows the full term + AIM and the user
  // sees ₹82,800 instead of the AIM-only ₹18,000 they're actually
  // committing to.
  const cartLevelSeatBlock = cartItems.some(
    (item) => item?.batchFormInfo?.isSeatBlock
  )
  return {
    cart_type: 'booking',
    parent: parentUuid,
    is_seat_block: cartLevelSeatBlock,
    items: cartItems.map((item, index) => ({
      item_id: item?.id,
      payment_cycle: item?.batchFormInfo?.paymentCycle?.value,
      additional_discounts: item?.batchFormInfo?.additionalDiscountOffered
        ? [item?.batchFormInfo?.additionalDiscount]
        : [],
      is_daycare_billing: Boolean(item?.batchFormInfo?.isDaycareBilling),
      is_seat_block: Boolean(item?.batchFormInfo?.isSeatBlock),
      program_fee: item?.batchFormInfo?.programFee,
      product: item.batchDetails.type,
      batch: item.batchDetails.uuid,
      student: item.userInfo.student.user.uuid,
      start_date: item.batchFormInfo.startDate.value,
      subject:
        item.batchDetails.program?.subjects?.[0]?.uuid ||
        item.batchDetails.course?.subjects?.[0]?.uuid,
      subject_internal_name:
        item.batchDetails.program?.subjects?.[0]?.internal_name ||
        item.batchDetails.course?.subjects?.[0]?.internal_name
    }))
  }
}
